import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { attributeFilterLabel, describeAttributeFilter } from '../../lib/attributeFilterDisplay';
import { resolveLocalizedText } from '../../lib/localizedText';
import type { AttributeDefinition, ProductAttributeSearchFilter } from '../../model/types';

export interface AttributeFiltersPopoverProps {
  /** Full attribute dictionary — filtered internally to `searchable === true`. */
  dictionary: AttributeDefinition[];
  active: ProductAttributeSearchFilter[];
  onChange: (filters: ProductAttributeSearchFilter[]) => void;
}

/**
 * `from`/`to` on `ProductAttributeSearchFilter` are typed as numbers in the contract (documented
 * for NUMBER/MEASUREMENT ranges), with no explicit DATE-range encoding described. Since the task
 * calls for a DATE from/to control, this encodes a date as epoch milliseconds (UTC midnight) — the
 * most standard numeric date representation — flagged as an assumption to verify server-side (see
 * user-story-merchant-portal-033's Notes). `lib/attributeFilterDisplay.ts` decodes it back for display.
 */
function dateToEpochMillis(dateStr: string): number | undefined {
  if (!dateStr) return undefined;
  const parsed = Date.parse(dateStr);
  return Number.isNaN(parsed) ? undefined : parsed;
}

/**
 * "Attribute filters" popover for `ProductsPage` (user-story-merchant-portal-033) — picks from
 * `searchable=true` attribute-dictionary definitions, one type-appropriate control per pick (TEXT
 * exact, SELECT option, BOOLEAN, NUMBER/MEASUREMENT from/to, DATE from/to), and surfaces active
 * filters as deletable entries (both inline here and as page-level chips the caller renders from
 * the same `active`/`onChange`).
 */
export function AttributeFiltersPopover({ dictionary, active, onChange }: AttributeFiltersPopoverProps) {
  const { t, i18n } = useTranslation('catalog');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [pendingCode, setPendingCode] = useState('');
  const [pendingValue, setPendingValue] = useState('');
  const [pendingFrom, setPendingFrom] = useState('');
  const [pendingTo, setPendingTo] = useState('');

  const searchable = useMemo(() => dictionary.filter((d) => d.searchable), [dictionary]);
  const availableToAdd = useMemo(
    () => searchable.filter((d) => !active.some((f) => f.code === d.code)),
    [searchable, active],
  );
  const pendingDefinition = searchable.find((d) => d.code === pendingCode) ?? null;

  const attributeLabel = (code: string): string => attributeFilterLabel(code, dictionary, i18n.language);

  const resetPending = () => {
    setPendingCode('');
    setPendingValue('');
    setPendingFrom('');
    setPendingTo('');
  };

  const canAddPending = (() => {
    if (!pendingDefinition) return false;
    switch (pendingDefinition.type) {
      case 'TEXT':
      case 'SELECT':
      case 'BOOLEAN':
        return pendingValue !== '';
      case 'NUMBER':
      case 'MEASUREMENT':
        return pendingFrom.trim() !== '' || pendingTo.trim() !== '';
      case 'DATE':
        return pendingFrom !== '' || pendingTo !== '';
      default:
        return false;
    }
  })();

  const addPendingFilter = () => {
    if (!pendingDefinition || !canAddPending) return;
    let entry: ProductAttributeSearchFilter;
    switch (pendingDefinition.type) {
      case 'BOOLEAN':
        entry = { code: pendingDefinition.code, value: pendingValue === 'true' };
        break;
      case 'DATE':
        entry = {
          code: pendingDefinition.code,
          from: dateToEpochMillis(pendingFrom),
          to: dateToEpochMillis(pendingTo),
        };
        break;
      case 'NUMBER':
      case 'MEASUREMENT':
        entry = {
          code: pendingDefinition.code,
          from: pendingFrom.trim() === '' ? undefined : Number(pendingFrom),
          to: pendingTo.trim() === '' ? undefined : Number(pendingTo),
        };
        break;
      default:
        entry = { code: pendingDefinition.code, value: pendingValue };
    }
    onChange([...active, entry]);
    resetPending();
  };

  const removeFilter = (code: string) => onChange(active.filter((f) => f.code !== code));

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<FilterAltRoundedIcon />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        {active.length > 0
          ? t('attributeFiltersPopover.buttonWithCount', { count: active.length })
          : t('attributeFiltersPopover.button')}
      </Button>

      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          resetPending();
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2.5, width: 340 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            {t('attributeFiltersPopover.title')}
          </Typography>

          {searchable.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('attributeFiltersPopover.noSearchableAttributes')}
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              <Autocomplete
                options={availableToAdd}
                value={pendingDefinition}
                onChange={(_e, value) => {
                  setPendingCode(value?.code ?? '');
                  setPendingValue('');
                  setPendingFrom('');
                  setPendingTo('');
                }}
                getOptionLabel={(option) => attributeLabel(option.code)}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                renderInput={(params) => (
                  <TextField {...params} label={t('attributeFiltersPopover.pickAttribute')} size="small" />
                )}
              />

              {pendingDefinition ? (
                <>
                  {pendingDefinition.type === 'TEXT' ? (
                    <TextField
                      label={t('attributeFiltersPopover.valueLabel')}
                      value={pendingValue}
                      onChange={(e) => setPendingValue(e.target.value)}
                      size="small"
                      fullWidth
                      autoFocus
                    />
                  ) : null}

                  {pendingDefinition.type === 'SELECT' ? (
                    <TextField
                      select
                      label={t('attributeFiltersPopover.valueLabel')}
                      value={pendingValue}
                      onChange={(e) => setPendingValue(e.target.value)}
                      size="small"
                      fullWidth
                    >
                      {(pendingDefinition.options ?? [])
                        .filter((o) => o.active)
                        .map((option) => (
                          <MenuItem key={option.code} value={option.code}>
                            {resolveLocalizedText(option.translations, 'label', i18n.language, option.code)}
                          </MenuItem>
                        ))}
                    </TextField>
                  ) : null}

                  {pendingDefinition.type === 'BOOLEAN' ? (
                    <TextField
                      select
                      label={t('attributeFiltersPopover.valueLabel')}
                      value={pendingValue}
                      onChange={(e) => setPendingValue(e.target.value)}
                      size="small"
                      fullWidth
                    >
                      <MenuItem value="true">{t('productAttributesPanel.booleanYes')}</MenuItem>
                      <MenuItem value="false">{t('productAttributesPanel.booleanNo')}</MenuItem>
                    </TextField>
                  ) : null}

                  {pendingDefinition.type === 'NUMBER' || pendingDefinition.type === 'MEASUREMENT' ? (
                    <Stack direction="row" spacing={1.5}>
                      <TextField
                        label={t('attributeFiltersPopover.fromLabel')}
                        type="number"
                        value={pendingFrom}
                        onChange={(e) => setPendingFrom(e.target.value)}
                        size="small"
                        fullWidth
                      />
                      <TextField
                        label={t('attributeFiltersPopover.toLabel')}
                        type="number"
                        value={pendingTo}
                        onChange={(e) => setPendingTo(e.target.value)}
                        size="small"
                        fullWidth
                      />
                    </Stack>
                  ) : null}

                  {pendingDefinition.type === 'DATE' ? (
                    <Stack direction="row" spacing={1.5}>
                      <TextField
                        label={t('attributeFiltersPopover.fromLabel')}
                        type="date"
                        value={pendingFrom}
                        onChange={(e) => setPendingFrom(e.target.value)}
                        size="small"
                        fullWidth
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                      <TextField
                        label={t('attributeFiltersPopover.toLabel')}
                        type="date"
                        value={pendingTo}
                        onChange={(e) => setPendingTo(e.target.value)}
                        size="small"
                        fullWidth
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                    </Stack>
                  ) : null}

                  <Box>
                    <Button size="small" variant="contained" onClick={addPendingFilter} disabled={!canAddPending}>
                      {t('attributeFiltersPopover.addFilter')}
                    </Button>
                  </Box>
                </>
              ) : null}
            </Stack>
          )}

          {active.length > 0 ? (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Stack spacing={0.75}>
                {active.map((filter) => (
                  <Stack key={filter.code} direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Typography variant="body2" noWrap>
                      {attributeLabel(filter.code)}: {describeAttributeFilter(filter, dictionary, i18n.language, t)}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => removeFilter(filter.code)}
                      aria-label={t('attributeFiltersPopover.removeAria', { label: attributeLabel(filter.code) })}
                    >
                      <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            </>
          ) : null}
        </Box>
      </Popover>
    </>
  );
}
